import Vue                                         from 'vue'
import Router                                      from 'vue-router'
import { getDefaultLocale, i18n, setI18nLanguage } from '@/lang/lang'

const MaintenanceMode = () => import(/* webpackChunkName: "home" */ '@/components/maintenance/MaintenanceMode')

const Home = () => import(/* webpackChunkName: "home" */ '@/components/home/Home')
const Menu = () => import(/* webpackChunkName: "menu" */ '@/components/menu/Menu')
const Contact = () => import(/* webpackChunkName: "contact" */ '@/components/contact/Contact')
const About = () => import(/* webpackChunkName: "about" */ '@/components/about/About')

const Checkout = () => import(/* webpackChunkName: "checkout" */ '@/components/cart/checkout/Checkout')
const CheckoutDetails = () => import(/* webpackChunkName: "checkout" */ '@/components/cart/checkout/CheckoutDetails')
const CheckoutPayment = () => import(/* webpackChunkName: "checkout" */ '@/components/cart/checkout/CheckoutPayment')
const CheckoutSuccess = () => import(/* webpackChunkName: "checkout" */ '@/components/cart/checkout/CheckoutSuccess')

const Account = () => import(/* webpackChunkName: "account" */ '@/components/account/Account')
const AccountUserProfile = () => import(/* webpackChunkName: "account" */ '@/components/account/AccountUserProfile')
const AccountAddresses = () => import(/* webpackChunkName: "account" */ '@/components/account/AccountAddresses')
const AccountOrders = () => import(/* webpackChunkName: "account" */ '@/components/account/AccountOrders')

const Policies = () => import(/* webpackChunkName: "policies" */ '@/components/policies/Policies')
const PoliciesCookies = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesCookies')
const PoliciesCopyright = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesCopyright')
const PoliciesPayments = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesPayments')
const PoliciesPrivacy = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesPrivacy')
const PoliciesReturns = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesReturns')
const PoliciesTerms = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesTerms')
const PoliciesAllergens = () => import(/* webpackChunkName: "policies" */ '@/components/policies/PoliciesAllergens')

if (window.location.href.endsWith('//')) {
  window.location.href = window.location.href.replace(/\/+$/, '')
}

Vue.use(Router)

const maintenanceModeCheck = (to, from, next) => {
  const inMaintenanceMode = window.AppConfig?.LOCATION_DATA?.MaintenanceMode ?? false

  // Handle maintenance mode access override ('mma') flag from query params and session storage.
  if (to?.query?.mma === 'skip') Vue.prototype.$sessionStorage.set('mma', true)
  if (!inMaintenanceMode) Vue.prototype.$sessionStorage.remove('mma')
  const hasSkipAccess = to?.query?.mma === 'skip' || Vue.prototype.$sessionStorage.get('mma') === true

  // Define a list of route names that should be accessible even when in maintenance mode.
  const excludedRoutes = ['MaintenanceMode']
  const isExcludedRoute = excludedRoutes.includes(to?.name)

  const shouldRedirectToMaintenance = inMaintenanceMode && !isExcludedRoute && !hasSkipAccess
  const shouldRedirectFromMaintenance = !inMaintenanceMode && to?.name === 'MaintenanceMode'

  if (shouldRedirectToMaintenance || shouldRedirectFromMaintenance) {
    const targetRouteName = shouldRedirectToMaintenance ? 'MaintenanceMode' : 'Home'

    // If in maintenance mode, redirect to the maintenance page (unless excluded or skipping).
    // If not in maintenance mode but on the maintenance page, redirect to the home page.
    next({
      replace: true,
      name   : targetRouteName,
      params : to?.params || { lang: i18n.locale },
      query  : to?.query || {}
    })
  }
}

const authCheck = (to, from, next) => {
  let userInfo = Vue.prototype.$localStorage.get('userInfo')
  let isUser = false
  if (userInfo) {
    try {
      userInfo = JSON.parse(userInfo) ? JSON.parse(userInfo) : null
      isUser = !!(userInfo && userInfo.Info && userInfo.Info.Id)
    } catch (err) {
      userInfo = null
    }
  }

  if (!isUser) {
    Vue.prototype.$auth.storage.removeItem('vueauth_access_token')
    Vue.prototype.$localStorage.remove('userInfo')
  }

  if (to.meta.requiresAuth) {
    if (!isUser) {
      next({
        name  : 'Home',
        params: { lang: i18n.locale }
      })
    }
  }

  next()
}

const routes = [
  {
    path     : '/:lang',
    props    : true,
    component: {
      render (c) {
        return c('router-view')
      }
    },
    children: [
      {
        name     : 'Home',
        path     : '',
        component: Home,
        meta     : {
          requiresAuth: false
        }
      },
      {
        name     : 'MaintenanceMode',
        path     : '',
        component: MaintenanceMode,
        meta     : {
          requiresAuth: false
        }
      },
      {
        name     : 'Menu',
        path     : 'catalog/:slug?/:menuItemSlug?',
        component: Menu,
        props    : true,
        meta     : {
          requiresAuth: false
        }
      },
      {
        name     : 'Order',
        path     : 'order',
        component: Checkout,
        redirect : { name: 'OrderStatus' },
        meta     : {
          requiresAuth: false
        },
        children: [
          {
            name     : 'OrderStatus',
            path     : 'status/:id?',
            component: CheckoutSuccess,
            meta     : {
              requiresAuth: false
            }
          }
        ]
      },
      {
        name     : 'Checkout',
        path     : 'checkout',
        component: Checkout,
        redirect : { name: 'CheckoutDetails' },
        meta     : {
          requiresAuth: false
        },
        children: [
          {
            name     : 'CheckoutDetails',
            path     : 'details',
            component: CheckoutDetails,
            props    : true,
            meta     : {
              requiresAuth: false
            }
          },
          {
            name     : 'CheckoutPayment',
            path     : 'payment',
            component: CheckoutPayment,
            meta     : {
              requiresAuth: false
            }
          },
          {
            name     : 'CheckoutSuccess',
            path     : 'success/:id?',
            component: CheckoutSuccess,
            meta     : {
              requiresAuth: false
            }
          }
        ]
      },
      {
        name     : 'Account',
        path     : 'account',
        component: Account,
        redirect : { name: 'AccountUserProfile' },
        meta     : {
          requiresAuth: true
        },
        children: [
          {
            name     : 'AccountUserProfile',
            path     : 'profile',
            component: AccountUserProfile,
            meta     : {
              requiresAuth: true
            }
          },
          {
            name     : 'AccountAddresses',
            path     : 'addresses',
            component: AccountAddresses,
            meta     : {
              requiresAuth: true
            }
          },
          {
            name     : 'AccountOrders',
            path     : 'orders',
            component: AccountOrders,
            meta     : {
              requiresAuth: true
            }
          }
        ]
      },
      {
        name     : 'Contact',
        path     : 'contact',
        component: Contact,
        meta     : {
          requiresAuth: false
        }
      },
      {
        name     : 'About',
        path     : 'about',
        component: About,
        meta     : {
          requiresAuth: false
        },
        beforeEnter: (to, from, next) => {
          next(window.AppConfig.PAGES.About.Pages[i18n.locale || 'el'].pages[to.name].status)
        }
      },
      {
        name     : 'Policies',
        path     : 'policies',
        component: Policies,
        redirect : { name: 'PoliciesTerms' },
        meta     : {
          requiresAuth: false
        },
        beforeEnter: (to, from, next) => {
          next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
        },
        children: [
          {
            name     : 'PoliciesTerms',
            path     : 'terms',
            component: PoliciesTerms,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesPrivacy',
            path     : 'privacy',
            component: PoliciesPrivacy,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesCopyright',
            path     : 'copyright',
            component: PoliciesCopyright,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesCookies',
            path     : 'cookies',
            component: PoliciesCookies,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesPayments',
            path     : 'payments',
            component: PoliciesPayments,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesReturns',
            path     : 'returns',
            component: PoliciesReturns,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          },
          {
            name     : 'PoliciesAllergens',
            path     : 'allergens',
            component: PoliciesAllergens,
            meta     : {
              requiresAuth: false
            },
            beforeEnter: (to, from, next) => {
              next(window.AppConfig.PAGES.Policies.Pages[i18n.locale || 'el'].pages[to.name].Status)
            }
          }
        ]
      }
    ]
  },
  {
    name    : 'StripePaymentSuccess',
    path    : '/payment/stripe/success/:id?',
    redirect: to => {
      const language = i18n.languages.find(lang => lang.code === to.query.lang)

      return {
        name  : 'CheckoutSuccess',
        params: {
          lang    : language?.locale ?? process.env.VUE_APP_I18N_LOCALE,
          id      : to.params.id || '',
          redirect: true
        },
        query: {}
      }
    },
    meta: {
      requiresAuth: false
    }
  },
  {
    name    : 'VivaPaymentSuccess',
    path    : '/payment/viva/success',
    redirect: to => {
      const language = i18n.languages.find(lang => lang.code === to.query.lang)
      return {
        name  : 'CheckoutSuccess',
        params: {
          lang    : language?.locale ?? process.env.VUE_APP_I18N_LOCALE,
          redirect: true
        },
        query: {}
      }
    },
    meta: {
      requiresAuth: false
    }
  },
  {
    name    : 'VivaPaymentFailure',
    path    : '/payment/viva/failure',
    redirect: to => {
      const language = i18n.languages.find(lang => lang.code === to.query.lang)
      return {
        name  : 'CheckoutPayment',
        params: {
          lang    : language?.locale ?? process.env.VUE_APP_I18N_LOCALE,
          redirect: true,
          error   : true
        },
        query: {}
      }
    },
    meta: {
      requiresAuth: false
    }
  }
]

const createRouter = () => new Router({
  mode  : 'history', // 'hash'
  base  : process.env.VUE_APP_PUBLIC_PATH,
  routes: [],
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return {
        x: 0,
        y: 0
      }
    }
  }
})

const router = createRouter()

const routerPush = Router.prototype.push
const routerReplace = Router.prototype.replace
Router.prototype.push = function push (location) {
  return routerPush.call(this, location).catch(error => error)
}
Router.prototype.replace = function replace (location) {
  return routerReplace.call(this, location).catch(error => error)
}

export function resetRouter () {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher

  routes.push({
    name    : 'Default',
    path    : '*',
    redirect: '/' + getDefaultLocale() || 'el'
  })
  router.addRoutes(routes)
}

router.beforeEach((to, from, next) => {
  const lang = to.params.lang || i18n.locale || getDefaultLocale()
  const langExists = i18n.messages.hasOwnProperty(lang)

  if (langExists) {
    setI18nLanguage(lang)
    maintenanceModeCheck(to, from, next)
    authCheck(to, from, next)
  } else {
    next({
      replace: true,
      name   : to.matched.length > 0 ? to.name : 'Home',
      params : { lang: i18n.locale }
    })
  }
})

export default router
